/**
 * Engraving Studio
 * Popup for personalizing a knife: pick a blade, check the surfaces to engrave, type each surface's
 * text, and see the active surface on a drawn blade.
 * Works on its own draft; Save hands the result back to the owning <engraving-selector>.
 */

// Scoped so the file can load twice (quick view re-injects its scripts) without redeclaring.
(() => {
  const ENGRAVING_STUDIO_MAX_CHARS = 100;

  // Blade silhouettes in a 480x140 box, tip pointing right, heel at x=110.
  // Each path starts at the heel on the spine and returns along the edge.
  const ENGRAVING_BLADE_SHAPES = {
    drop: 'M110 38 H360 C410 38 450 50 470 66 C440 92 400 104 340 104 H110 Z',
    clip: 'M110 38 H320 Q410 64 472 62 C442 92 400 104 340 104 H110 Z',
    spear: 'M110 38 H340 C400 40 445 55 472 71 C445 87 400 102 340 104 H110 Z',
    wharncliffe: 'M110 38 H300 C380 40 440 70 472 104 H110 Z',
    sheepsfoot: 'M110 38 H400 C440 38 466 60 470 104 H110 Z',
    spey: 'M110 38 H440 C460 40 470 52 468 62 C452 90 420 104 380 104 H110 Z',
    tanto: 'M110 38 H420 L472 60 L440 104 H110 Z',
    reverseTanto: 'M110 38 H360 L472 70 L420 104 H110 Z',
    hawkbill: 'M110 38 H330 C420 38 470 70 466 118 C440 100 400 104 340 104 H110 Z',
    skinner: 'M110 46 H300 C380 46 440 30 476 22 C450 70 400 104 330 104 H110 Z',
  };

  // Style names as entered on products -> silhouette (first match wins, fallback: drop)
  const ENGRAVING_STYLE_MATCHERS = [
    [/reverse\s*tanto/i, 'reverseTanto'],
    [/tanto/i, 'tanto'],
    [/clip|bowie/i, 'clip'],
    [/spear|leaf|dagger|needle|pen/i, 'spear'],
    [/wharn/i, 'wharncliffe'],
    [/sheep|straight\s*back|cotton|coping|lamb|spatula/i, 'sheepsfoot'],
    [/spey/i, 'spey'],
    [/hawk|talon|karambit/i, 'hawkbill'],
    [/skinner|trailing|upswept/i, 'skinner'],
  ];

  function engravingShapeFor(style) {
    const match = ENGRAVING_STYLE_MATCHERS.find(([pattern]) => pattern.test(style || ''));
    return ENGRAVING_BLADE_SHAPES[match ? match[1] : 'drop'];
  }

  function engravingSurfaceKey(bladeId, side) {
    return `${bladeId}-${side}`;
  }

  function engravingSanitizeText(value) {
    // Preserve letters in every language, numbers, spaces, and intentional line breaks.
    return String(value || '').replace(/[^\p{L}\p{N} \n]/gu, '');
  }

  class EngravingStudio extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.svg = this.querySelector('[data-studio-svg]');
      this.bladePath = this.querySelector('[data-studio-blade]');
      this.knifeShape = this.querySelector('[data-studio-shape]');
      this.textNode = this.querySelector('[data-studio-text]');
      this.previewLabel = this.querySelector('[data-studio-preview-label]');
      this.bladeList = this.querySelector('[data-studio-blades]');
      this.summary = this.querySelector('[data-studio-summary]');
      this.summaryDetails = this.querySelector('[data-studio-summary-details]');
      this.previewInfo = this.querySelector('[data-studio-preview-info]');
      this.previewTooltip = this.querySelector('[data-studio-preview-tooltip]');
      this.saveButton = this.querySelector('[data-studio-save]');
      this.panel = this.querySelector('[data-studio-panel]');
      this.modeButtons = this.querySelectorAll('[data-studio-mode-button]');

      this.priceCents = parseInt(this.dataset.priceCents, 10) || 0;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

      this.querySelectorAll('[data-studio-close]').forEach((button) =>
        button.addEventListener('click', () => this.close())
      );
      // Click outside the dialog box (on the backdrop) closes without saving. Both the press and
      // the release must land outside, so a text selection dragged out of a field does not close it.
      const outsideDialog = (event) => {
        const rect = this.dialog.getBoundingClientRect();
        return (
          event.clientX < rect.left || event.clientX > rect.right ||
          event.clientY < rect.top || event.clientY > rect.bottom
        );
      };
      let pressedOutside = false;
      this.dialog.addEventListener('pointerdown', (event) => {
        pressedOutside = outsideDialog(event);
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog && pressedOutside && outsideDialog(event)) this.close();
        pressedOutside = false;
      });
      this.saveButton.addEventListener('click', () => this.save());
      this.summary.addEventListener('click', () => this.toggleSummary());
      this.previewInfo.addEventListener('click', () => this.togglePreviewInfo());
      this.querySelector('[data-studio-preview-close]').addEventListener('click', () => this.closePreviewInfo());
      this.modeButtons.forEach((button) =>
        button.addEventListener('click', () => this.setMode(button.dataset.studioModeButton))
      );
    }

    /** Text engraves in the studio; custom artwork hands off to customer service. */
    setMode(mode) {
      this.panel.dataset.mode = mode;
      this.modeButtons.forEach((button) =>
        button.setAttribute('aria-pressed', String(button.dataset.studioModeButton === mode))
      );
    }

    /**
     * Open the studio for a product config and the current engravings.
     * @param {Object} config - { blades: [{ id, style, front, back }] }
     * @param {Array} engravings - [{ blade, side, text, font }]
     * @param {Function} onSave - receives the saved engravings array
     */
    open(config, engravings, onSave) {
      this.config = config;
      this.onSave = onSave;
      this.draft = new Map(); // surface key -> text
      this.checked = new Set(); // surface keys the customer ticked
      (engravings || []).forEach((engraving) => {
        const key = engravingSurfaceKey(engraving.blade, engraving.side);
        this.draft.set(key, engravingSanitizeText(engraving.text));
        this.checked.add(key);
      });

      // Blades with an engraving start checked; a single-blade knife is always checked
      this.checkedBlades = new Set((engravings || []).map((engraving) => engraving.blade));
      if (config.blades.length === 1) this.checkedBlades.add(config.blades[0].id);

      const firstEngraved = (engravings || [])[0];
      const startBlade = firstEngraved ? firstEngraved.blade : config.blades[0].id;
      const startConfig = config.blades.find((blade) => blade.id === startBlade);
      this.activeBlade = startBlade;
      this.activeSide = firstEngraved ? firstEngraved.side : startConfig.front ? 'front' : 'back';

      this.renderBlades();
      this.renderPreview();
      this.renderStatus();
      this.setMode('text');
      this.dialog.showModal();
      document.documentElement.classList.add('engraving-studio-open');
    }

    close() {
      this.dialog.close();
      document.documentElement.classList.remove('engraving-studio-open');
    }

    save() {
      // A ticked side needs text: point the customer at the first empty one instead of saving
      const empty = this.checkedSurfaces().find(({ key }) => !(this.draft.get(key) || '').trim());
      if (empty) {
        const field = this.bladeList.querySelector(`[data-surface-input="${empty.key}"]`);
        field?.closest('.engraving-studio__surface')?.classList.add('is-missing');
        field?.focus({ preventScroll: true });
        field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const engravings = this.checkedSurfaces().map(({ blade, side, key }) => ({
        blade: blade.id,
        side,
        text: engravingSanitizeText(this.draft.get(key)).trim(),
        font: 'Bodoni MT',
      }));
      if (this.onSave) this.onSave(engravings);
      this.close();
    }

    /** Ticked surfaces in blade/side order: [{ blade, side, key }] */
    checkedSurfaces() {
      const surfaces = [];
      this.config.blades.forEach((blade) => {
        ['front', 'back'].forEach((side) => {
          const key = engravingSurfaceKey(blade.id, side);
          if (blade[side] && this.checked.has(key)) surfaces.push({ blade, side, key });
        });
      });
      return surfaces;
    }

    /**
     * Blade checklist: checking a blade reveals checkboxes for its available surfaces;
     * checking a surface reveals its text box. A single-blade knife skips the blade checkbox.
     */
    renderBlades() {
      const single = this.config.blades.length === 1;
      this.bladeList.innerHTML = '';
      this.config.blades.forEach((blade) => {
        const entry = document.createElement('div');
        entry.className = 'engraving-studio__blade-entry';
        entry.dataset.bladeId = blade.id;

        const surfaces = document.createElement('div');
        surfaces.className = 'engraving-studio__blade-surfaces';
        ['front', 'back'].forEach((side) => {
          if (blade[side]) surfaces.appendChild(this.createSurface(blade, side));
        });

        if (!single) {
          const label = document.createElement('label');
          label.className = 'engraving-studio__check engraving-studio__check--blade';
          const checkbox = document.createElement('input');
          checkbox.type = 'checkbox';
          checkbox.checked = this.checkedBlades.has(blade.id);
          const box = document.createElement('span');
          box.className = 'engraving-studio__check-box';
          box.setAttribute('aria-hidden', 'true');
          const name = document.createElement('span');
          name.className = 'engraving-studio__check-label';
          name.textContent = `Blade ${blade.id} · ${blade.style}`;
          label.append(checkbox, box, name);
          checkbox.addEventListener('change', () => this.toggleBlade(blade, checkbox.checked, surfaces));
          entry.appendChild(label);
          surfaces.hidden = !checkbox.checked;
        } else {
          surfaces.classList.add('is-single');
        }

        entry.appendChild(surfaces);
        this.bladeList.appendChild(entry);
      });
    }

    toggleBlade(blade, checked, surfaces) {
      if (checked) {
        this.checkedBlades.add(blade.id);
        surfaces.hidden = false;
        this.setActive(blade.id, blade.front ? 'front' : 'back');
        surfaces.querySelector('[data-surface-check]')?.focus();
      } else {
        // Unchecking a blade drops its surfaces and their text
        this.checkedBlades.delete(blade.id);
        ['front', 'back'].forEach((side) => {
          const key = engravingSurfaceKey(blade.id, side);
          this.checked.delete(key);
          this.draft.delete(key);
        });
        surfaces.querySelectorAll('.engraving-studio__surface').forEach((surface) => {
          surface.classList.remove('is-missing');
          surface.querySelector('[data-surface-check]').checked = false;
          surface.querySelector('[data-surface-input]').value = '';
          surface.querySelector('[data-surface-count]').textContent = `0/${ENGRAVING_STUDIO_MAX_CHARS}`;
          surface.querySelector('.engraving-studio__surface-field').hidden = true;
        });
        surfaces.hidden = true;
        this.setActive(blade.id, blade.front ? 'front' : 'back');
      }
      this.renderStatus();
    }

    createSurface(blade, side) {
      const key = engravingSurfaceKey(blade.id, side);
      const sideLabel = side === 'front' ? 'Front' : 'Back';
      const surface = document.createElement('div');
      surface.className = 'engraving-studio__surface';

      const label = document.createElement('label');
      label.className = 'engraving-studio__check';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = this.checked.has(key);
      checkbox.dataset.surfaceCheck = key;
      const box = document.createElement('span');
      box.className = 'engraving-studio__check-box';
      box.setAttribute('aria-hidden', 'true');
      const text = document.createElement('span');
      text.className = 'engraving-studio__check-label';
      text.textContent = sideLabel;
      const price = document.createElement('span');
      price.className = 'engraving-studio__check-price';
      price.textContent = `+${this.formatMoney(this.priceCents)}`;
      label.append(checkbox, box, text, price);

      const field = document.createElement('div');
      field.className = 'engraving-studio__surface-field';
      field.hidden = !checkbox.checked;
      const input = document.createElement('textarea');
      input.className = 'engraving-studio__input';
      input.rows = 2;
      input.maxLength = ENGRAVING_STUDIO_MAX_CHARS;
      input.placeholder = `Type your ${sideLabel.toLowerCase()} engraving`;
      input.value = this.draft.get(key) || '';
      input.dataset.surfaceInput = key;
      input.setAttribute('aria-label', `Engraving text for blade ${blade.id}, ${side} (${blade.style})`);
      const meta = document.createElement('div');
      meta.className = 'engraving-studio__field-meta';
      const hint = document.createElement('span');
      hint.className = 'engraving-studio__field-hint';
      hint.textContent = 'Add text or uncheck this side';
      const count = document.createElement('span');
      count.className = 'engraving-studio__count';
      count.dataset.surfaceCount = key;
      count.textContent = `${input.value.length}/${ENGRAVING_STUDIO_MAX_CHARS}`;
      meta.append(hint, count);
      field.append(input, meta);

      checkbox.addEventListener('change', () => {
        if (checkbox.checked) {
          this.checked.add(key);
          field.hidden = false;
          this.setActive(blade.id, side);
          input.focus();
        } else {
          this.checked.delete(key);
          this.draft.delete(key);
          input.value = '';
          count.textContent = `0/${ENGRAVING_STUDIO_MAX_CHARS}`;
          field.hidden = true;
          surface.classList.remove('is-missing');
          this.setActive(blade.id, side);
        }
        this.renderStatus();
      });
      input.addEventListener('focus', () => this.setActive(blade.id, side));
      input.addEventListener('input', () => {
        const sanitized = engravingSanitizeText(input.value).slice(0, ENGRAVING_STUDIO_MAX_CHARS);
        if (input.value !== sanitized) input.value = sanitized;
        if (input.value.trim()) {
          this.draft.set(key, input.value);
          surface.classList.remove('is-missing');
        } else {
          this.draft.delete(key);
        }
        count.textContent = `${input.value.length}/${ENGRAVING_STUDIO_MAX_CHARS}`;
        this.setActive(blade.id, side);
        this.renderStatus();
      });

      surface.append(label, field);
      return surface;
    }

    setActive(bladeId, side) {
      this.activeBlade = bladeId;
      this.activeSide = side;
      this.renderPreview();
    }

    toggleSummary() {
      if (!this.checked.size) return;
      const isOpen = this.summary.getAttribute('aria-expanded') === 'true';
      this.summary.setAttribute('aria-expanded', String(!isOpen));
      this.summaryDetails.hidden = isOpen;
      if (!isOpen) {
        requestAnimationFrame(() => {
          this.summaryDetails.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }

    togglePreviewInfo() {
      const isOpen = this.previewInfo.getAttribute('aria-expanded') === 'true';
      if (isOpen) this.closePreviewInfo();
      else {
        this.previewInfo.setAttribute('aria-expanded', 'true');
        this.previewTooltip.hidden = false;
      }
    }

    closePreviewInfo() {
      this.previewInfo.setAttribute('aria-expanded', 'false');
      this.previewTooltip.hidden = true;
      this.previewInfo.focus();
    }

    /**
     * Fit the text onto the blade flat: shrink the font, then wrap to up to 3 lines.
     */
    renderPreview() {
      const blade = this.config.blades.find((candidate) => candidate.id === this.activeBlade) || this.config.blades[0];
      const side = this.activeSide;
      // Front view shows the tip pointing left (shapes are drawn tip-right); back mirrors it to the right
      this.bladePath.setAttribute('d', engravingShapeFor(blade.style));
      this.knifeShape.setAttribute('transform', side === 'front' ? 'translate(480 0) scale(-1 1)' : '');
      this.previewLabel.textContent = `Previewing blade ${blade.id} · ${side === 'front' ? 'Front' : 'Back'}`;

      const text = (this.draft.get(engravingSurfaceKey(blade.id, side)) || '').trim();
      const maxWidth = 210;
      const centerX = 240;
      const centerY = 71;
      const node = this.textNode;
      node.textContent = '';
      if (!text) return;

      const setLines = (lines, size) => {
        node.textContent = '';
        node.setAttribute('font-size', size);
        const lineHeight = size * 1.15;
        const firstY = centerY - ((lines.length - 1) * lineHeight) / 2;
        lines.forEach((line, index) => {
          const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
          tspan.setAttribute('x', centerX);
          tspan.setAttribute('y', firstY + index * lineHeight);
          tspan.textContent = line;
          node.appendChild(tspan);
        });
      };
      const fits = () => Array.from(node.childNodes).every((tspan) => tspan.getComputedTextLength() <= maxWidth);
      const splitInto = (count) => {
        const words = text.split(/\s+/);
        if (words.length < count) return [text];
        const target = text.length / count;
        const lines = [];
        let current = '';
        words.forEach((word) => {
          const next = current ? `${current} ${word}` : word;
          if (next.length > target && current && lines.length < count - 1) {
            lines.push(current);
            current = word;
          } else {
            current = next;
          }
        });
        lines.push(current);
        return lines;
      };

      // Preserve a customer's intentional line breaks before considering automatic wrapping.
      const manualLines = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      if (manualLines.length > 1 && manualLines.length <= 3) {
        const maxSize = manualLines.length === 2 ? 17 : 13;
        for (let size = maxSize; size >= 8; size -= 1) {
          setLines(manualLines, size);
          if (fits()) return;
        }
      }

      for (const lineCount of [1, 2, 3]) {
        const lines = lineCount === 1 ? [text] : splitInto(lineCount);
        const maxSize = lineCount === 1 ? 24 : lineCount === 2 ? 17 : 13;
        for (let size = maxSize; size >= 8; size -= 1) {
          setLines(lines, size);
          if (fits()) return;
        }
      }
    }

    renderStatus() {
      const surfaces = this.checked.size;
      this.summary.textContent = surfaces ? 'Show summary' : `${this.formatMoney(this.priceCents)} each`;
      this.saveButton.textContent = surfaces
        ? `Add ${surfaces} ${surfaces === 1 ? 'engraving' : 'engravings'} + knife to cart`
        : 'No engraving - Add knife to cart';
      this.summary.disabled = surfaces === 0;
      this.summary.setAttribute('aria-expanded', 'false');
      this.summaryDetails.hidden = true;
      this.summaryDetails.innerHTML = '';

      if (!surfaces) return;

      const total = document.createElement('p');
      total.className = 'engraving-studio__summary-total';
      total.textContent = `${surfaces} ${surfaces === 1 ? 'surface' : 'surfaces'} selected · +${this.formatMoney(surfaces * this.priceCents)}`;
      const list = document.createElement('ul');
      list.className = 'engraving-studio__summary-list';
      this.checkedSurfaces().forEach(({ blade, side, key }) => {
        const item = document.createElement('li');
        item.className = 'engraving-studio__summary-item';
        const surface = document.createElement('span');
        surface.className = 'engraving-studio__summary-surface';
        surface.textContent = `Blade ${blade.id} · ${side === 'front' ? 'Front' : 'Back'}`;
        const engraving = document.createElement('span');
        engraving.className = 'engraving-studio__summary-text';
        const text = (this.draft.get(key) || '').trim();
        engraving.textContent = text ? `“${text}”` : 'No text yet';
        item.append(surface, engraving);
        list.appendChild(item);
      });
      this.summaryDetails.append(total, list);
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return this.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, amount);
    }
  }

  if (!customElements.get('engraving-studio')) {
    customElements.define('engraving-studio', EngravingStudio);
  }
})();
